import React, { PropsWithChildren, useContext, useMemo } from "react";
import { PublicConfigurationContext } from "../Context";
import { getAICoachConfig } from "@aidkitorg/types/lib/config";
import { ChatConversationProvider } from "../Contexts/ChatContext";
import { AudioInterfaceProvider } from "../Contexts/AudioInterfaceContext";
import { DefaultChatSessionProvider } from "../Contexts/ChatSessionContext";
import { ApplicantContext } from "../Contexts/ApplicantContext";
import { evalConditional } from "@aidkitorg/roboscreener/lib/compute/util";
import { CompileExpressionToJS } from "@aidkitorg/types/lib/translation/expr_to_js";

export type AICoachState = {
  enabled: boolean,
  config?: ReturnType<typeof getAICoachConfig>,
};

const defaults: AICoachState = {
  enabled: false
}

export const AICoachContext = React.createContext<AICoachState>(defaults);

export function AICoachProvider({ children }: PropsWithChildren): JSX.Element {

  const config = useContext(PublicConfigurationContext);
  const { info } = useContext(ApplicantContext);

  const enabled = useMemo(() => {
    if (!config.capabilities?.aiCoach || config.capabilities.aiCoach.disabled) {
      return false;
    }

    // Check subsurvey restriction
    const limitToSubsurveys = config.capabilities.aiCoach.limitToSubsurveys ?? [];
    const currentSubsurvey = window.location.pathname.replace(/^\/(p\/)*/ig, '').split('/')[0].toLowerCase();
    if (limitToSubsurveys.length && !limitToSubsurveys.includes(currentSubsurvey)) {
      return false;
    }

    // Check allowedApplicants expression
    if (config.capabilities.aiCoach.allowedApplicants) {
      const expression = CompileExpressionToJS(config.capabilities.aiCoach.allowedApplicants);
      const allowed = evalConditional(expression, info, {}, {});
      return !!allowed;
    }

    return true;
  }, [config.capabilities?.aiCoach, info]);

  const stateValue: AICoachState = {
    config: config.capabilities?.aiCoach,
    enabled
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return <AudioInterfaceProvider>
    <ChatConversationProvider>
      <DefaultChatSessionProvider>
        <AICoachContext.Provider value={stateValue}>
          {children}
        </AICoachContext.Provider>
      </DefaultChatSessionProvider>
    </ChatConversationProvider>
  </AudioInterfaceProvider>;
}

// sugar
export function useAICoach(): AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}
